import {
    ComponentManager,
    ControlFactory,
    DataTransferObject,
    DetailViewContext,
    GlobalConfiguration,
    GroupFactory,
    GroupFactoryTuple,
    ListViewViewModel,
    Loader,
    Messenger,
    RADApplication,
    TaskUIListenerHelper,
    ToolBarItemViewModel,
    UIListener,
    UIListenerHelper
} from "radpresentation.htmlclient.defaultviewextensions";
import WFEUserTaskContainerDTO from '@/Communication/WFEUserTaskContainerDTO';
import CoreFrameDTOs from "coreframetypings/dto";

export default class TaskUIListener extends UIListener {

    private workflowOperations: any;
    private lastWorkflowOperation: any;
    private taskPropsErgNames: string[] = [
        'WFE.WORKFLOWENGINE.MODEL.TASK_TITLE_ERGNAME',
        'WFE.WORKFLOWENGINE.MODEL.TASK_DESCRIPTION_ERGNAME',
        'WFE.WORKFLOWENGINE.MODEL.TASK_ISCOMPLETED_ERGNAME'
    ];

    public constructor() {
        super();

        this.workflowOperations = {};
        this.lastWorkflowOperation = null;
    }

    private static changeTaskTabGroupIfNecessary(context: DetailViewContext): void {
        if (RADApplication.getInstance().user.isSuperUser())
            return;

        const pdTypeName = context.getConcretePdTypeName();
        const dvGroup = UIListenerHelper.getDetailViewGroupDto(context, `${pdTypeName}.BESCHREIBUNG`);
        dvGroup.uiGenerator = dvGroup.uiGenerator.replace('TabGroup', 'ExpanderGroup');
        UIListenerHelper.removeDetailViewGroupDto(context, `${pdTypeName}.TASKCONTROLLING`);
    }

    /**
     * @override
     */
    public onBeforeRender(context: DetailViewContext): void {
        TaskUIListener.changeTaskTabGroupIfNecessary(context);
        TaskUIListenerHelper.onBeforeRender(context);
        this.hideTaskItems(context);
        
        const container = this.getTaskContainer(context);
        this.hideFinishedMethodIfNecessary(container, context);
    }

    /**
     * @override
     */
    public onBeforeRenderSummaryView(context: DetailViewContext): void {
        TaskUIListener.changeTaskTabGroupIfNecessary(context);
        TaskUIListenerHelper.onBeforeRenderSummaryView(context);
    }

    /**
     * @override
     */
    public onSummaryViewReady(context: DetailViewContext): void {
        TaskUIListenerHelper.onSummaryViewReady(context);

        // Wird für WizardGroup benötigt. Wir müssen auf das onPartialRendered des PrimaryInputObjects horchen
        this.registerToNestedContextOnPartialRendered(context);
    }

    /**
     * @override
     */
    public onDetailViewReady(context: DetailViewContext): void {
        Messenger.createMessageAndSend('RefreshUserTasksMessage');

        // Wird für WizardGroup benötigt. Wir müssen auf das onPartialRendered des PrimaryInputObjects horchen
        this.registerToNestedContextOnPartialRendered(context);
    }

    /**
     * @override
     */
    public onDetailViewClosed(context: DetailViewContext): void {
        Messenger.createMessageAndSend('RefreshUserTasksMessage');
    }

    /**
     * @override
     */
    public onBeforeCallOperation(context: DetailViewContext | ListViewViewModel, operation: string): boolean | JQueryPromise<boolean> | JQueryDeferred<boolean> {
        const result = TaskUIListenerHelper.onBeforeCallOperation(context, operation);
        if (result != null)
            return result;

        return super.onBeforeCallOperation(context, operation);
    }

    /**
     * @override
     */
    public onAfterCallOperation(context: DetailViewContext, operation: string, args: CoreFrameDTOs.IOperationResultDTO): void {
        if (TaskUIListenerHelper.onAfterCallOperation(context, operation, args))
            return;

        if (!(context instanceof DetailViewContext)) {
            Messenger.createMessageAndSend('RefreshUserTasksMessage');
            return;
        }

        if (operation === 'CallUserTaskOperation' && args.status === 'Successfull' && args.returnValue === true) {
            if (this.lastWorkflowOperation in this.workflowOperations && this.workflowOperations[this.lastWorkflowOperation].IsControlFlowOperation() === true) {
                context.save();
            }
        }

        if (operation === 'SignalFinished') {
            context.save();
        }

        this.lastWorkflowOperation = null;
        Messenger.createMessageAndSend('RefreshUserTasksMessage');
    }

    /**
     * @override
     */
    public onPartialRendered(context: DetailViewContext): void {
        const container = this.getTaskContainer(context);
        if (!container)
            return;

        const isLocked = context.isLocked();
        this.updateInputObjectItems(container, context, isLocked);
    }

    /**
     * @override
     */
    public onAfterRefresh(context: DetailViewContext): void {
        if (!(context instanceof DetailViewContext)) {
            return;
        }

        const container = this.getTaskContainer(context);
        if (!container)
            return;

        const isCompleted = context.genericObject.getValue('IsCompleted');
        const isLocked = context.isLocked();

        // Wurde eigentlich nach onBeforeRender verschoben.
        // Dieser Aufruf dient nur als Fallback, sollte es eine Konstellation geben,
        // in der onBeforeRender nicht ausreichend ist.
        this.hideFinishedMethodIfNecessary(container, context);
        
        // Da einige der Toolbar-Buttons ggf. via Container.UIConfigurations deaktiviert werden, merken wir uns diese jetzt.
        // Die dynamisch via Container.Operations angelegten Buttons werden nicht via UIConfigurations ausgeblendet.
        let secondaryItems = context.toolBar.secondary();

        this.addUserTaskOperations(container, context, isCompleted, isLocked);
        this.updateInputObjectOperations(container, context, secondaryItems, isLocked);
        this.updateInputObjectItems(container, context, isLocked);
        this.disableTaskItems(context, isCompleted, isLocked);
    }

    private hideFinishedMethodIfNecessary(container: WFEUserTaskContainerDTO, context: DetailViewContext) {
        // Wenn Workflow-Operationen definiert werden, brauchen wir den Standard-Button nicht mehr.
        // TODO: Prüfen, ob die Bedingung nicht verschärft werden sollte?
        //       -> Mindestens eine *ControlFlowOperation* vorhanden
        if (container.Operations.length > 0) {
            context.toolBar.removeSecondaryElementByErgName('WFE.WORKFLOWENGINE.MODEL.TASK_SIGNALFINISHED_ERGNAME');
        }
    }

    private getTaskContainer(context: DetailViewContext): WFEUserTaskContainerDTO {
        let xml = context.genericObject.getValue('UserTaskContainerDTO');
        if (xml == null || xml === '')
            return null;

        return DataTransferObject.parseFromXML(xml) as WFEUserTaskContainerDTO;
    }

    private registerToNestedContextOnPartialRendered(context: DetailViewContext): void {
        const nestedcontext = this.getNestedContext(context);
        if (!nestedcontext)
            return;

        const baseMethod = nestedcontext.notifyPartialRendered.bind(nestedcontext);
        nestedcontext.notifyPartialRendered = () => {
            baseMethod();
            this.onPartialRendered(context);
        }
    }

    private getNestedContext(context: DetailViewContext): DetailViewContext {
        const primaryInputObjectTuple = context.getCreatedTuple('WFE.WorkflowEngine.Model.Task.PRIMARYINPUTOBJECT') as GroupFactoryTuple;
        const primaryInputObjectFactory = primaryInputObjectTuple?.factory;
        const nestedItemName = primaryInputObjectFactory?.detailViewGroup.items[0]?.name;
        if (!nestedItemName)
            return null;

        const nestedContexts = context._nestedDetailViewContexts();
        return nestedContexts[nestedItemName];
    }

    private updateInputObjectItems(container: WFEUserTaskContainerDTO, context: DetailViewContext, isLocked: boolean): void {
        const nestedcontext = this.getNestedContext(context);
        if (!nestedcontext)
            return;

        // TODO: Das Ausblenden der Factory funktioniert nicht
        let metadataGroup = nestedcontext.getUIFactory<GroupFactory>('*METADATA');
        if (metadataGroup != null) {
            metadataGroup.isVisible(false);
        }

        if (container.UIConfigurations != null) {
            for (let i = 0; i < container.UIConfigurations.length; i++) {
                let configuration = container.UIConfigurations[i];

                //Fall 1: Ist es einfach Factory?
                let factory = nestedcontext.getUIFactory<ControlFactory>(configuration.Name());
                if (factory != null) {
                    factory.isVisible(configuration.Visible());
                    factory.isEnabled(!isLocked && configuration.Editable());
                }
            }
        }

        let groupTuples = nestedcontext.getCreatedGroups();
        for (let j = 0; j < groupTuples.length; j++) {
            let groupTuple = groupTuples[j];
            this.hideGroupIfNoElementsVisible(groupTuple);
        }
    }

    private hideTaskItems(context: DetailViewContext): void {
        const taskDescriptionGroup = this.getDetailViewGroupDto(context, 'WFE.WorkflowEngine.Model.Task.BESCHREIBUNG');

        if (taskDescriptionGroup) {
            this.taskPropsErgNames.forEach(propErgName => this.setEmptyUIGenerator(propErgName, taskDescriptionGroup.items));
        }
    }

    private setEmptyUIGenerator(propErgName: string, items: CoreFrameDTOs.IDVGItemDTO[]): void {
        const itemIndex = _.findIndex(items, i => i.ergName === propErgName);

        if (itemIndex !== -1)
            items[itemIndex].uiGenerator = 'EmptyElement';
    }

    private disableTaskItems(context: DetailViewContext, isCompleted: boolean, isLocked: boolean): void {
        if (isLocked || isCompleted === true) {
            let groupTuples = context.getCreatedGroups();

            for (let i = 0; i < groupTuples.length; i++) {
                let itemTuples = groupTuples[i].getCreatedControlTuples();

                for (let j = 0; j < itemTuples.length; j++) {
                    itemTuples[j].factory.isEnabled(false);
                }
            }
        } else if (!RADApplication.getInstance().user.isSuperUser()) {
            let descriptionGroupTuple = context.getCreatedTuple('WFE.WorkflowEngine.Model.Task.BESCHREIBUNG') as GroupFactoryTuple;
            let itemTuples = descriptionGroupTuple.getCreatedControlTuples();

            for (let i = 0; i < itemTuples.length; i++) {
                itemTuples[i].factory.isEnabled(false);
            }
        }
    }

    private updateInputObjectOperations(container: WFEUserTaskContainerDTO, context: DetailViewContext, secondaryItems: ToolBarItemViewModel[], isLocked: boolean): void {
        for (let i = 0; i < secondaryItems.length; i++) {
            let item = secondaryItems[i];

            if (context.toolBar.isLink(item) && item.label != null) {
                let techName = item.technicalName;
                let ergName = item.label.ergName();

                if (ergName && ergName !== 'EDIT_METADATA_BUTTON_TEXT') {
                    for (let j = 0; j < container.UIConfigurations.length; j++) {
                        let uiConfiguration = container.UIConfigurations[j];
                        let name = uiConfiguration.Name();

                        if (name === techName) {
                            // Wir haben eine UIConfiguration gefunden, die sich auf den Toolbar-Link 'link' bezieht,
                            // nun die Sichtbarkeit/Anklickbarkeit anwenden.
                            item.isVisible(uiConfiguration.Visible());
                            item.isEnabled(!isLocked && uiConfiguration.Editable());
                            break;
                        }
                    }
                }
            }
        }
    }

    private addUserTaskOperations(container: WFEUserTaskContainerDTO, context: DetailViewContext, isCompleted: boolean, isLocked: boolean): void {
        for (let i = 0; i < container.Operations.length; i++) {
            let operation = container.Operations[i];

            let methodName = operation.MethodName();
            let iconUrl = operation.IconUrl();
            let title = operation.Title();
            let locator = methodName;
            let tooltip = operation.ShortDescription();

            this.workflowOperations[methodName] = operation;

            context.toolBar.removeSecondaryElementByCaption(title);

            context.toolBar.addSecondaryElement({
                text: title,
                locator: locator,
                tooltipText: tooltip,
                isEnabled: !isCompleted && !isLocked,
                iconUrl: iconUrl || GlobalConfiguration.inlineImages.GEAR,
                onClick: () => {
                    Loader.getInstance().show();
                    let context = RADApplication.getInstance().currentDetailViewContext();
                    $.when(context.updateAndValidateProperties()).then(updateAndValidateSuccess => {
                        if (updateAndValidateSuccess !== false) {
                            this.lastWorkflowOperation = methodName;
                            context.callOperation('CallUserTaskOperation', [methodName]);
                        }
                        Loader.getInstance().hide();
                    });
                }
            });
        }
    }

    private hideGroupIfNoElementsVisible(groupTuple: GroupFactoryTuple): void {
        let itemTuples = groupTuple.getCreatedControlTuples(true);
        let isVisible = false;

        for (let i = 0; i < itemTuples.length; i++) {
            if (itemTuples[i].factory.isVisible() === true) {
                isVisible = true;
                break;
            }
        }

        if (isVisible === false) {
            groupTuple.factory.isVisible(false);
        }
    }
}

ComponentManager.setUIListener('WorkflowEngineViewExtension.UIListeners.Model.TaskUIListener', TaskUIListener, 'WorkflowEngineHTMLViewExtension');